import { useQuery } from '@tanstack/react-query'
import { resourcesCopy } from '@/copy/resources'
import { CheckboxField, SelectField, TextField } from '@/ui/Field/Field'
import { Text } from '@/ui/layout/Text'
import { ingressApi } from '../../api/ingress'
import { topicsApi } from '../../api/topics'
import { FieldGroup } from './FieldGroup'
import { PredicateFields } from './PredicateFields'
import { predicateBody, predicateFields } from './predicate'
import {
  type FieldProps,
  type Fields,
  fieldStr,
  type ResourceFormDescriptor,
} from './shared'

/** Keys the form doesn't edit — a predicate's `exists`, the tag of the
 * recipe that installed the route — carried through edits verbatim. */
const PRESERVED_KEYS = ['exists', 'recipe'] as const

/** Route policy flags: what an event's lifecycle fact means in this topic. */
const POLICY_FLAGS = [
  ['resolve_on_closed', resourcesCopy.forms.eventRoute.resolveOnClosed],
  ['supersede_on_updated', resourcesCopy.forms.eventRoute.supersedeOnUpdated],
] as const

/**
 * The event_route form (rev4.1 R3): select a source connection, pick a
 * target topic (workspace scope only — topic scope self-targets), write a
 * predicate over the connection's normalized events, and set the route's
 * policy — what the events' facts mean in this topic.
 *
 * Routes are pure config (no tools, no actor); the emit path evaluates
 * them. The creation scope constrains the target: workspace routes pick
 * any topic in the workspace, topic routes are their own target (D2).
 * Source and target are fixed at creation (the server rejects changes).
 */
// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function EventRouteFields({ fields, set, scope, isEdit }: FieldProps) {
  const isWorkspace = scope?.kind === 'workspace'

  // Topic scope lists its workspace's connections: resolve the workspace
  // from the topic first.
  const { data: topic } = useQuery({
    queryKey: ['topic', scope?.id ?? ''],
    queryFn: () => topicsApi.get(scope?.id ?? ''),
    enabled: scope?.kind === 'topic',
    staleTime: 30_000,
  })
  const workspaceId = isWorkspace ? scope.id : topic?.workspace_id

  // Every resource whose kind receives events — the server decides.
  const { data: sources = [] } = useQuery({
    queryKey: ['route-sources', workspaceId ?? ''],
    queryFn: () => ingressApi.routeSources(workspaceId ?? ''),
    enabled: !!workspaceId,
    staleTime: 30_000,
  })

  // Target options: the workspace's topics (workspace scope only).
  const { data: topics } = useQuery({
    queryKey: ['topics', scope?.id ?? ''],
    queryFn: () => topicsApi.listForWorkspace(scope!.id),
    enabled: isWorkspace,
    staleTime: 30_000,
  })

  // Source and target are server-fixed after creation: a read-only text
  // field showing the value, not a disabled select (D202).
  const sourceId = fields.source_resource_id ?? ''
  const source = sources.find((s) => s.id === sourceId)
  const targetId = fields.target_topic_id ?? ''
  const target = (topics ?? []).find((t) => String(t.topic_id) === targetId)

  return (
    <>
      {isEdit ? (
        <TextField
          id="route-source"
          label={resourcesCopy.forms.eventRoute.source}
          value={
            source
              ? resourcesCopy.forms.eventRoute.sourceOption(
                  source.name,
                  source.kind,
                )
              : sourceId
          }
          onValue={() => {}}
          help={resourcesCopy.forms.eventRoute.fixedHelp}
          readOnly
        />
      ) : (
        <SelectField
          id="route-source"
          label={resourcesCopy.forms.eventRoute.source}
          value={sourceId}
          onValue={(v) => set('source_resource_id', v)}
        >
          <option value="">
            {resourcesCopy.forms.eventRoute.chooseSource}
          </option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {resourcesCopy.forms.eventRoute.sourceOption(s.name, s.kind)}
            </option>
          ))}
        </SelectField>
      )}

      {isWorkspace && isEdit && (
        <TextField
          id="route-target"
          label={resourcesCopy.forms.eventRoute.target}
          value={target?.name ?? targetId}
          onValue={() => {}}
          help={resourcesCopy.forms.eventRoute.fixedHelp}
          readOnly
        />
      )}
      {isWorkspace && !isEdit && (
        <SelectField
          id="route-target"
          label={resourcesCopy.forms.eventRoute.target}
          value={targetId}
          onValue={(v) => set('target_topic_id', v)}
        >
          <option value="">{resourcesCopy.forms.eventRoute.chooseTopic}</option>
          {(topics ?? []).map((t) => (
            <option key={t.topic_id} value={String(t.topic_id)}>
              {t.name}
            </option>
          ))}
        </SelectField>
      )}
      {!isWorkspace && (
        <Text as="p" variant="caption" tone="text-mute">
          {resourcesCopy.forms.eventRoute.selfTarget}
        </Text>
      )}

      <PredicateFields fields={fields} set={set} idPrefix="route" />

      <CheckboxField
        id="route-paused"
        label={resourcesCopy.forms.eventRoute.paused}
        checked={fields.enabled === 'false'}
        onChecked={(v) => set('enabled', v ? 'false' : '')}
      />

      <CheckboxField
        id="route-fallback"
        label={resourcesCopy.forms.eventRoute.fallback}
        checked={fields.fallback === 'true'}
        onChecked={(v) => set('fallback', v ? 'true' : '')}
      />

      <PolicyFields fields={fields} set={set} />
    </>
  )
}

/** The route's behavior in its topic. */
// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function PolicyFields({ fields, set }: Pick<FieldProps, 'fields' | 'set'>) {
  return (
    <FieldGroup label={resourcesCopy.forms.eventRoute.behaviour}>
      {POLICY_FLAGS.map(([key, label]) => (
        <CheckboxField
          key={key}
          id={`route-${key}`}
          label={label}
          checked={fields[key] === 'true'}
          onChecked={(v) => set(key, v ? 'true' : '')}
        />
      ))}
      <TextField
        id="route-coalesce"
        label={resourcesCopy.forms.eventRoute.coalesce}
        help={resourcesCopy.forms.eventRoute.coalesceHelp}
        value={fields.coalesce_s ?? ''}
        onValue={(v) => set('coalesce_s', v)}
        inputProps={{ inputMode: 'decimal', placeholder: '0' }}
      />
      <TextField
        id="route-max-events"
        label={resourcesCopy.forms.eventRoute.maxEvents}
        help={resourcesCopy.forms.eventRoute.maxEventsHelp}
        value={fields.max_correlated_events ?? ''}
        onValue={(v) => set('max_correlated_events', v)}
        inputProps={{ inputMode: 'numeric', placeholder: '200' }}
      />
    </FieldGroup>
  )
}

/** A positive integer form value, or undefined when blank/invalid (the
 * server validates ranges). */
function intOrUndefined(v: string | undefined): number | undefined {
  const n = Number.parseInt(v ?? '', 10)
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

export const eventRouteForm: ResourceFormDescriptor = {
  Fields: EventRouteFields,
  toBody: (f: Fields) => {
    const base: Record<string, unknown> = {
      source_resource_id: f.source_resource_id ?? '',
      ...predicateBody(f),
    }
    if (f.fallback === 'true') base.fallback = true
    for (const [key] of POLICY_FLAGS) if (f[key] === 'true') base[key] = true
    const seconds = Number.parseFloat(f.coalesce_s ?? '')
    if (Number.isFinite(seconds) && seconds >= 0)
      base.coalesce_ms = Math.round(seconds * 1000)
    if (f.enabled === 'false') base.enabled = false
    const maxEvents = intOrUndefined(f.max_correlated_events)
    if (maxEvents !== undefined) base.max_correlated_events = maxEvents
    // target_topic_id is workspace-scope only; topic-scoped routes
    // self-target and must NOT carry the field (validation rejects it).
    if (f.target_topic_id) base.target_topic_id = f.target_topic_id
    return base
  },
  fromResource: (d: unknown) => {
    const r = (d ?? {}) as Record<string, unknown>
    const flag = (k: string) => (r[k] === true ? 'true' : '')
    const num = (k: string) => (typeof r[k] === 'number' ? String(r[k]) : '')
    return {
      ...predicateFields(d, PRESERVED_KEYS),
      source_resource_id: fieldStr(d, 'source_resource_id'),
      target_topic_id: fieldStr(d, 'target_topic_id'),
      fallback: flag('fallback'),
      resolve_on_closed: flag('resolve_on_closed'),
      supersede_on_updated: flag('supersede_on_updated'),
      coalesce_s:
        typeof r.coalesce_ms === 'number' ? String(r.coalesce_ms / 1000) : '',
      enabled: r.enabled === false ? 'false' : '',
      max_correlated_events: num('max_correlated_events'),
    }
  },
}
